// Static corpus artifact shapes shipped to public/corpus/ at build time.
// Distinct from Convex `retellMaster` block storage: this is lekterion's
// "one string + offset overlays" model that the reader fetches over HTTP.
// Zod schemas validate the JSON on fetch; TS types are inferred from them.

// Namespace import, not `import { z }` — zod ships CJS for the node
// condition and the named form resolves to `undefined` under vitest's
// multi-project run. Same fix as packages/logging/src/ingest.ts; it is
// zod 4's documented import form anyway.
import * as z from "zod";
import type { BlockKind } from "@bindery/convex/model/block";

/** Character range [start, end) into a DocumentModel's `text`. */
export const offsetSpanSchema = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});
export type OffsetSpan = z.infer<typeof offsetSpanSchema>;

/**
 * Block kind for the static artifact. `satisfies` pins these values to the
 * shared `BlockKind` (convex/model/block) so the corpus can never carry a kind
 * the retelling store doesn't know about. `verse-line` appears only in ORIGINAL
 * models (continuous-verse per-line split); retellings never carry it.
 */
export const documentBlockKindSchema = z.enum([
  "paragraph",
  "heading",
  "verse-line",
]) satisfies z.ZodType<BlockKind>;

/**
 * One block overlay. Retelling-side blocks carry the stable block id minted
 * at seed time (anchors/bookmarks key on it); originals have no ids.
 */
export const documentBlockSchema = offsetSpanSchema.extend({
  id: z.string().optional(),
  kind: documentBlockKindSchema.optional(),
});
export type DocumentBlock = z.infer<typeof documentBlockSchema>;

export const documentHeadingSchema = offsetSpanSchema.extend({
  /** Heading depth; 1 = chapter title. */
  level: z.number().int().positive(),
});
export type DocumentHeading = z.infer<typeof documentHeadingSchema>;

/** Token overlay, used for selection snapping in the original pane. */
export const documentTokenSchema = offsetSpanSchema;
export type DocumentToken = z.infer<typeof documentTokenSchema>;

/**
 * Emphasis run overlay (italic/bold spans from the source Markdown, e.g.
 * `*Peter*` in the peter_pan rewrites) — kept so inline emphasis survives
 * into the reader instead of flattening to plain text (lekterion `Emphasis`).
 */
export const documentEmphasisSchema = offsetSpanSchema;
export type DocumentEmphasis = z.infer<typeof documentEmphasisSchema>;

/** One chapter side (original or retelling) as built by the ingest pipeline. */
export const documentModelSchema = z.object({
  text: z.string(),
  blocks: z.array(documentBlockSchema),
  headings: z.array(documentHeadingSchema),
  tokens: z.array(documentTokenSchema),
  emphasis: z.array(documentEmphasisSchema),
});
export type DocumentModel = z.infer<typeof documentModelSchema>;
